import React, { Fragment, useContext, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';

import { PageDocumentContext } from '@confluence/content-renderer-extension-context/entry-points/PageDocumentContext.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { TableOfContentsLoader } from './TableOfContentsLoader.tsx';

// The legacy server-side TOC plugin looks for this class and mutates the DOM via jQuery.
// We use it only to find the original server-rendered node, then replace that node entirely
// with a neutral mount node so the legacy plugin no longer owns the DOM subtree.
const LEGACY_TOC_SELECTOR = '.client-side-toc-macro[data-macro-id]';
// This selector is for the replacement mount node we create ourselves after swapping out the
// legacy server-rendered TOC node. It lets subsequent effect runs find and reuse the
// frontend-owned node instead of trying to replace the original server-rendered TOC again.
const HYDRATED_TOC_MOUNT_SELECTOR = '[data-fe-toc-mount="true"][data-macro-id]';

export type NestedLegacyTocHydratorProps = {
	container: Element | null;
	sectionNode: any;
	contentId?: string;
	spaceKey?: string;
	references?: ExtensionHandlerProps['references'];
};

type NestedLegacyTocMount = {
	macroId: string;
	mountNode: HTMLDivElement;
	tocNode: any;
};

/**
 * Walk the migrated section ADF to find the nested TOC node that matches the server-rendered
 * TOC node we discovered in the DOM. That lets us preserve the original TOC params when we
 * re-render it on the frontend.
 */
const findNestedTocNodeByMacroId = (node?: any, macroId?: string) => {
	if (!node || !macroId) {
		return null;
	}

	const nodeMacroId = node?.attrs?.parameters?.macroMetadata?.macroId?.value;
	if (node?.attrs?.extensionKey === 'toc' && nodeMacroId === macroId) {
		return node;
	}

	for (const child of node?.content || []) {
		const nestedMatch = findNestedTocNodeByMacroId(child, macroId);
		if (nestedMatch) {
			return nestedMatch;
		}
	}

	const nestedContent = node?.attrs?.parameters?.nestedContent;
	if (nestedContent) {
		return findNestedTocNodeByMacroId(nestedContent, macroId);
	}

	return null;
};

export const NestedLegacyTocHydrator = ({
	container,
	sectionNode,
	contentId,
	spaceKey,
	references,
}: NestedLegacyTocHydratorProps): React.JSX.Element | null => {
	const fullPageDocument = useContext(PageDocumentContext);
	const { cloudId, userId } = useSessionData();
	const [tocMounts, setTocMounts] = useState<NestedLegacyTocMount[]>([]);

	// Find any legacy server-rendered TOC nodes inside this section render, replace them with stable
	// frontend-owned mount nodes, and remember which nested TOC node belongs to each rendered TOC.
	useLayoutEffect(() => {
		if (!container || !fullPageDocument) {
			setTocMounts([]);
			return;
		}

		const nextTocMounts: NestedLegacyTocMount[] = [];
		container
			.querySelectorAll<HTMLElement>(`${LEGACY_TOC_SELECTOR}, ${HYDRATED_TOC_MOUNT_SELECTOR}`)
			.forEach((tocElement) => {
				const macroId = tocElement.dataset.macroId || '';
				const tocNode = findNestedTocNodeByMacroId(sectionNode, macroId);

				if (!macroId || !tocNode) {
					return;
				}

				let mountNode: HTMLDivElement;
				if (tocElement.dataset.feTocMount === 'true') {
					mountNode = tocElement as HTMLDivElement;
				} else {
					mountNode = document.createElement('div');
					mountNode.dataset.feTocMount = 'true';
					mountNode.dataset.macroId = macroId;
					tocElement.parentNode?.replaceChild(mountNode, tocElement);
				}

				nextTocMounts.push({ macroId, mountNode, tocNode });
			});

		setTocMounts(nextTocMounts);
	}, [container, fullPageDocument, sectionNode]);

	if (!fullPageDocument || tocMounts.length === 0) {
		return null;
	}

	return (
		<Fragment>
			{tocMounts.map(({ macroId, mountNode, tocNode }) => {
				// Render the standard frontend TOC into the neutral mount node that replaced the
				// legacy server-rendered TOC node, while still passing through the original nested TOC node.
				const macroDefaultProps: ExtensionHandlerProps = {
					node: tocNode,
					references,
					contentId: contentId || '',
					spaceKey: spaceKey || '',
					mode: RENDERER,
					extensionKey: 'toc',
					attributes: { macroId, extensionKey: 'toc' },
				};

				return createPortal(
					<TableOfContentsLoader
						adf={fullPageDocument}
						cloudId={cloudId || ''}
						userId={userId || ''}
						macroDefaultProps={macroDefaultProps}
						isInEditMode={false}
					/>,
					mountNode,
					macroId,
				);
			})}
		</Fragment>
	);
};
